import React from 'react';
import { Typography, Link as MuiLink } from '@material-ui/core';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';

import { dedupe } from '@hisports/parsers';

import { getSurfaceAttributes, getSurfaceName } from '../../../../resources/surfaces/util';

import { SNAP_MINUTES, getEventId, getEventResource } from '../../DragAndDropGrid';

export const buildDayCalendarColumns = ({
  selectedGame,
  filteredSurfaces,
  availability,
  colOffset,
  maxColumns,
  initialStart,
  timezone,
  translate,
  getAlert,
  setColOffset,
  gameLength,
}) => {
  const hasDate = selectedGame?.date != null;
  const values = hasDate ? filteredSurfaces.slice(colOffset, colOffset + maxColumns) : [];
  const header = hasDate ? moment.utc(selectedGame?.date).format('LL') : ' - ';
  const effectiveIds = dedupe(filteredSurfaces?.map(surface => surface.effectiveSurfaceIds).flat()) || [];

  return {
    header,
    disabled: !hasDate || !values.length,
    alert: getAlert(values),
    columns: values,
    totalColumns: filteredSurfaces.length,
    start: selectedGame?.date || initialStart,
    getColumnKey: surface => surface.id,
    getColumnIndex: event =>
      values.findIndex(surface =>
        (event.positionArenaId || event.arenaId) === surface.id ||
        (event.targetType === 'Surface' &&
          (event.positionArenaId || event.targetId) === surface.id)
      ),
    getColumnHeading: surface => {
      const attributes = getSurfaceAttributes(surface, translate);
      return (
        <>
          <Link component={MuiLink} to={`/surfaces/${surface.id}/show`} target="_blank">
            {getSurfaceName(surface)}
          </Link>
          <Typography variant="body2" color="textSecondary">{attributes}</Typography>
        </>
      );
    },
    filterEvents: events => {
      const date = selectedGame?.date && moment(selectedGame.date);
      const dateEvents = events.filter(event => {
        if (
          (event.targetType === 'Surface' ? !event.targetId : !event.arenaId) ||
          !event.startTime ||
          !event.endTime
        ) return false;

        if (date && !date.isSame(event.date, 'day')) return false;

        return !event.targetType ? effectiveIds.includes(event.arenaId) : event.targetType === 'Surface' && effectiveIds.includes(event.targetId);
      });

      return values.map(surface =>
        dateEvents.filter(event => !event.targetType ? (event.positionArenaId || event.arenaId) === surface.id : event.targetType === 'Surface' && (event.positionArenaId || event.targetId) === surface.id)
      );
    },
    pushSplitSurfaceEvents: events => {
      const newEvents = [];

      events.forEach(event => {
        const eventSurface = (availability?.surfaces || []).find(({ id }) => id === (event.arenaId || event.targetId));
        if (!eventSurface?.effectiveSurfaceIds) return;

        const newEventSurfaces = eventSurface.effectiveSurfaceIds.filter(surfaceId => surfaceId !== (event.arenaId || event.targetId) && filteredSurfaces.find(({ id }) => id === surfaceId));
        newEventSurfaces.forEach(surfaceId => {
          newEvents.push({ ...event, positionArenaId: surfaceId, isEditable: false, isSplitSurfaceEvent: true });
        });
      });

      return [...events, ...newEvents];
    },
    toEventDetails: (item, eventResource, eventId) => {
      const { x: itemColumn, y: itemRow, h: itemHeight } = item;
      const resource = eventResource || getEventResource(item);
      const id = eventId || getEventId(item);

      const date = selectedGame?.date;
      const startOfDay = { hours: 6, minutes: 0, seconds: 0 };
      const startTime = moment.tz(date, timezone).set(startOfDay).add(itemRow * SNAP_MINUTES, 'minutes').toISOString();

      const endTime = resource === 'games'
        ? moment.tz(date, timezone).set(startOfDay).add((itemRow + itemHeight) * SNAP_MINUTES, 'minutes').toISOString()
        : moment.tz(startTime, timezone).add(gameLength || 60, 'minutes').toISOString();

      const arena = values?.[itemColumn];

      return { id, resource, date, startTime, endTime, arenaId: arena?.id };
    },
    onNext: () => setColOffset(colOffset + 1),
    onBack: () => setColOffset(colOffset - 1),
  };
};
